import { useEffect, useRef } from "react";
import { useFormikContext } from "formik";
import useDebounce from "../../hooks/useDebounce";

const FieldAvailabilityWatcher = ({
  fieldName,
  setStatus,
  triggerCheck,
  shouldSkip,
  errorMessage,
  debounceMs = 600,
}) => {
  const { values, setFieldError } = useFormikContext();
  const fieldValue = values[fieldName];
  const debouncedValue = useDebounce(fieldValue, debounceMs);

  // Track the last checked value to avoid redundant API calls
  const lastCheckedRef = useRef("");

  useEffect(() => {
    // Caller decides whether the value is ready for an API hit
    if (shouldSkip(debouncedValue)) {
      setStatus("idle");
      lastCheckedRef.current = "";
      return;
    }

    // Skip identical consecutive checks
    if (debouncedValue === lastCheckedRef.current) {
      return;
    }

    const checkAvailability = async () => {
      setStatus("checking");
      try {
        const res = await triggerCheck(debouncedValue).unwrap();
        lastCheckedRef.current = debouncedValue;

        if (res.availability) {
          setStatus("available");
          setFieldError(fieldName, undefined); // clear error
        } else {
          setStatus("taken");
          setFieldError(fieldName, errorMessage);
        }
      } catch (err) {
        console.error(`[FieldAvailabilityWatcher] ${fieldName} check failed:`, err);
        setStatus("idle");
      }
    };

    checkAvailability();
  }, [debouncedValue, fieldName, setStatus, triggerCheck, errorMessage, setFieldError]);

  return null;
};

export default FieldAvailabilityWatcher;
